import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, PlayCircle, Loader2, ArrowLeft, Lock, Award, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CoursePlayer() {
  const { courseId } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [active, setActive] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [denied, setDenied] = useState(false);
  const [mode, setMode] = useState("lessons"); // lessons | quiz
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [quizPassed, setQuizPassed] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get(`/courses/${courseId}/content`).then((r) => {
      setCourse(r.data);
      setCompleted(r.data.completed_lessons || []);
      setQuizPassed(r.data.quiz_passed || false);
    }).catch((e) => { if (e.response?.status === 403) setDenied(true); else toast.error("Could not load course"); });
  }, [courseId, user, loading, navigate]);

  const markComplete = async (lessonId, goNext) => {
    try {
      await api.post(`/courses/${courseId}/progress`, { lesson_id: lessonId });
      setCompleted((prev) => prev.includes(lessonId) ? prev : [...prev, lessonId]);
    } catch (e) { console.error("mark complete failed", e); }
    if (goNext && active < course.lessons.length - 1) setActive(active + 1);
    else if (goNext) setMode("quiz");
  };

  const submitQuiz = async () => {
    const arr = course.quiz.map((_, i) => answers[i] ?? -1);
    if (arr.some((a) => a === -1)) { toast.error("Please answer every question"); return; }
    try {
      const { data } = await api.post(`/courses/${courseId}/quiz`, { answers: arr });
      setResult(data);
      if (data.passed) { setQuizPassed(true); toast.success(`Passed with ${data.score}%!`); }
      else toast.error(`Scored ${data.score}%. 70% needed to pass.`);
    } catch { toast.error("Could not submit quiz"); }
  };

  if (denied) return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center">
      <Lock className="w-14 h-14 text-slate-300 mx-auto mb-4" />
      <h1 className="font-display text-2xl font-bold text-blue-950">Purchase required</h1>
      <p className="mt-2 text-slate-600">Enroll in this course to unlock the lessons.</p>
      <Button asChild className="mt-6 bg-blue-900 text-white"><Link to={`/store/${courseId}`}>View course</Link></Button>
    </div>
  );
  if (!course) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const lesson = course.lessons[active];
  const pct = Math.round((completed.length / course.lessons.length) * 100);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-blue-800 mb-5 hover:gap-2.5 transition-all"><ArrowLeft className="w-4 h-4" /> My Portal</Link>
      <h1 className="font-display text-2xl lg:text-3xl font-bold text-blue-950 mb-1">{course.name}</h1>
      <div className="flex items-center gap-3 mb-6">
        <div className="h-2 w-48 rounded-full bg-blue-100 overflow-hidden"><div className="h-full bg-blue-700 transition-all" style={{ width: `${pct}%` }} /></div>
        <span className="text-sm text-slate-500">{pct}% complete</span>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        <div>
          {mode === "lessons" ? (
            <motion.div key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              {lesson.image_url ? (
                <div className="rounded-2xl overflow-hidden shadow-lg bg-slate-900">
                  <img src={lesson.image_url} alt={lesson.title} className="w-full aspect-video object-cover" data-testid="lesson-image" />
                </div>
              ) : (
                <div className="rounded-2xl overflow-hidden bg-black shadow-lg">
                  <video key={lesson.video_url} controls className="w-full aspect-video" data-testid="lesson-video">
                    <source src={lesson.video_url} type="video/mp4" />
                  </video>
                </div>
              )}
              <h2 className="font-display text-xl font-semibold text-blue-950 mt-5">{lesson.title}</h2>
              {lesson.module && <p className="text-xs uppercase tracking-wide text-teal-600 font-semibold mt-1">{lesson.module}</p>}
              {lesson.audio_url && (
                <div className="mt-4 bg-blue-950 rounded-xl p-3.5 flex items-center gap-3" data-testid="lesson-audio-wrap">
                  <span className="text-xs font-semibold text-teal-300 shrink-0 pl-1">▶ Narration</span>
                  <audio key={lesson.audio_url} controls className="w-full h-9" data-testid="lesson-audio">
                    <source src={lesson.audio_url} type="audio/mpeg" />
                  </audio>
                </div>
              )}
              <p className="text-slate-600 mt-4 leading-relaxed">{lesson.body || lesson.description}</p>
              {lesson.objectives?.length > 0 && (
                <div className="mt-4 bg-teal-50/70 border border-teal-100 rounded-xl p-4" data-testid="lesson-objectives">
                  <p className="text-xs uppercase tracking-wide text-teal-700 font-semibold mb-2">What you'll learn</p>
                  <ul className="space-y-1.5">
                    {lesson.objectives.map((o, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" /> {o}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex gap-3 mt-6">
                <Button data-testid="mark-complete-button" onClick={() => markComplete(lesson.id, true)} className="bg-blue-900 hover:bg-blue-950 text-white gap-2">
                  <CheckCircle2 className="w-4 h-4" /> {active < course.lessons.length - 1 ? "Complete & Next" : "Complete & Take Quiz"}
                </Button>
                {active > 0 && <Button variant="outline" onClick={() => setActive(active - 1)}>Previous</Button>}
              </div>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm">
              <div className="flex items-center gap-2 mb-5"><ClipboardCheck className="w-6 h-6 text-blue-800" /><h2 className="font-display text-xl font-semibold text-blue-950">Final Quiz</h2></div>
              {quizPassed && !result && <div className="mb-4 bg-blue-50 text-blue-800 rounded-lg px-4 py-2.5 text-sm">You've already passed this quiz. Retake it anytime.</div>}
              <div className="space-y-6">
                {course.quiz.map((q, qi) => (
                  <div key={qi} data-testid={`quiz-q-${qi}`}>
                    <p className="font-medium text-blue-950">{qi + 1}. {q.q}</p>
                    <div className="mt-2 space-y-2">
                      {q.options.map((opt, oi) => (
                        <label key={oi} className={`flex items-center gap-2.5 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${answers[qi] === oi ? "border-blue-600 bg-blue-50" : "border-blue-900/10 hover:bg-slate-50"}`}>
                          <input type="radio" name={`q${qi}`} checked={answers[qi] === oi} onChange={() => setAnswers({ ...answers, [qi]: oi })} data-testid={`quiz-q-${qi}-opt-${oi}`} />
                          <span className="text-sm text-slate-700">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {result && (
                <div className={`mt-5 rounded-lg px-4 py-3 text-sm font-medium ${result.passed ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`} data-testid="quiz-result">
                  You scored {result.score}% ({result.correct}/{result.total}). {result.passed ? "Congratulations, you passed!" : "70% is needed to pass — review and try again."}
                </div>
              )}
              <div className="flex flex-wrap gap-3 mt-6">
                <Button onClick={submitQuiz} data-testid="submit-quiz-button" className="bg-blue-900 hover:bg-blue-950 text-white">Submit Quiz</Button>
                <Button variant="outline" onClick={() => setMode("lessons")}>Back to lessons</Button>
                {(quizPassed || result?.passed) && (
                  <Button asChild className="bg-amber-500 hover:bg-amber-600 text-white gap-2"><Link to={`/certificate/${courseId}`} data-testid="get-certificate-button"><Award className="w-4 h-4" /> Get Certificate</Link></Button>
                )}
              </div>
            </motion.div>
          )}
        </div>

        <aside className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1">Lessons</p>
          {course.lessons.map((l, i) => (
            <button key={l.id} data-testid={`lesson-nav-${l.id}`} onClick={() => { setMode("lessons"); setActive(i); }}
              className={`w-full text-left flex items-center gap-3 rounded-xl px-3.5 py-3 border transition-colors ${active === i && mode === "lessons" ? "border-blue-600 bg-blue-50" : "border-blue-900/10 bg-white hover:bg-slate-50"}`}>
              {completed.includes(l.id) ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" /> : <PlayCircle className="w-5 h-5 text-blue-700 shrink-0" />}
              <span className="text-sm min-w-0"><span className="block font-medium text-blue-950 truncate">{l.title}</span><span className="text-xs text-slate-400">{l.duration}</span></span>
            </button>
          ))}
          <button data-testid="quiz-nav" onClick={() => setMode("quiz")}
            className={`w-full text-left flex items-center gap-3 rounded-xl px-3.5 py-3 border transition-colors ${mode === "quiz" ? "border-blue-600 bg-blue-50" : "border-blue-900/10 bg-white hover:bg-slate-50"}`}>
            {quizPassed ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" /> : <ClipboardCheck className="w-5 h-5 text-blue-700 shrink-0" />}
            <span className="text-sm font-medium text-blue-950">Final Quiz & Certificate</span>
          </button>
        </aside>
      </div>
    </div>
  );
}
